// The signed content of a CMS SignedData (PKCS#7) package: the bytes of encapContentInfo's
// eContent, exactly as the sender signed them. Null when the bytes are not such a package or the
// content is detached. DER and BER (indefinite lengths, constructed OCTET STRING) are read.
export function getCmsSignedContent(bytes: Uint8Array): Uint8Array | null {
  try {
    const contentInfo = readTlv(bytes, 0);
    if (contentInfo.tag !== 0x30) {
      return null;
    }
    // ContentInfo: contentType (signedData), [0] EXPLICIT SignedData.
    const [contentType, explicitContent] = readChildren(bytes, contentInfo);
    if (contentType?.tag !== 0x06 || explicitContent?.tag !== 0xa0) {
      return null;
    }
    // SignedData: version, digestAlgorithms, encapContentInfo, ...
    const signedData = readChildren(bytes, explicitContent)[0];
    const encapContentInfo = readChildren(bytes, signedData)[2];
    // EncapsulatedContentInfo: eContentType, [0] EXPLICIT eContent (absent when detached).
    const eContent = readChildren(bytes, encapContentInfo)[1];
    if (eContent?.tag !== 0xa0) {
      return null;
    }
    const octetString = readChildren(bytes, eContent)[0];
    return octetString ? readOctets(bytes, octetString) : null;
  } catch {
    return null;
  }
}

interface Tlv {
  tag: number;
  constructed: boolean;
  // Value bounds; for an indefinite length, up to the end-of-contents octets.
  start: number;
  end: number;
  // The position after the element.
  next: number;
}

function readTlv(bytes: Uint8Array, position: number): Tlv {
  let pos = position;
  const tag = bytes[pos++];
  if (tag === undefined) {
    throw new Error('CMS: unexpected end');
  }
  if ((tag & 0x1f) === 0x1f) {
    while (bytes[pos++] & 0x80) {
      // high tag number: skipped
    }
  }

  const first = bytes[pos++];
  if (first === 0x80) {
    // Indefinite length: children up to the end-of-contents octets 00 00.
    const start = pos;
    while (!(bytes[pos] === 0 && bytes[pos + 1] === 0)) {
      if (pos >= bytes.length) {
        throw new Error('CMS: unterminated element');
      }
      pos = readTlv(bytes, pos).next;
    }
    return { tag, constructed: (tag & 0x20) !== 0, start, end: pos, next: pos + 2 };
  }

  let length = first;
  if (first & 0x80) {
    length = 0;
    for (let count = first & 0x7f; count > 0; count--) {
      length = length * 256 + bytes[pos++];
    }
  }
  const end = pos + length;
  if (end > bytes.length) {
    throw new Error('CMS: length out of range');
  }
  return { tag, constructed: (tag & 0x20) !== 0, start: pos, end, next: end };
}

function readChildren(bytes: Uint8Array, parent: Tlv): Tlv[] {
  const children: Tlv[] = [];
  for (let pos = parent.start; pos < parent.end; ) {
    const child = readTlv(bytes, pos);
    children.push(child);
    pos = child.next;
  }
  return children;
}

// An OCTET STRING's bytes; a constructed one is the concatenation of its parts.
function readOctets(bytes: Uint8Array, element: Tlv): Uint8Array {
  if (!element.constructed) {
    return bytes.slice(element.start, element.end);
  }
  const parts = readChildren(bytes, element).map((part) => readOctets(bytes, part));
  const result = new Uint8Array(parts.reduce((sum, part) => sum + part.length, 0));
  let offset = 0;
  for (const part of parts) {
    result.set(part, offset);
    offset += part.length;
  }
  return result;
}
